export const SYSTEM_LOG_SCHEMA_VERSION = 3;

export const SYSTEM_LOG_EVENT_KINDS = Object.freeze([
  'technical_error',
  'business_validation',
  'user_input',
  'access_rejected',
  'transient_failure',
  'security_event',
  'diagnostic_warning',
  'external_failure',
]);

export const SYSTEM_LOG_SEVERITIES = Object.freeze([
  'critical',
  'high',
  'medium',
  'low',
  'informational',
]);

const BUILD_ENV = Object.freeze({
  REACT_APP_SYSTEM_VERSION: process.env.REACT_APP_SYSTEM_VERSION || '',
  REACT_APP_SYSTEM_ENVIRONMENT: process.env.REACT_APP_SYSTEM_ENVIRONMENT || '',
  REACT_APP_BUILD_ID: process.env.REACT_APP_BUILD_ID || '',
  REACT_APP_BUILD_DATE: process.env.REACT_APP_BUILD_DATE || '',
  REACT_APP_GIT_COMMIT: process.env.REACT_APP_GIT_COMMIT || '',
  REACT_APP_SYSTEM_PORTAL: process.env.REACT_APP_SYSTEM_PORTAL || '',
  NODE_ENV: process.env.NODE_ENV || '',
});

const readBuildValue = (name) => String(BUILD_ENV[name] || '').trim();

const sanitizeLabeledFileNames = (value = '') => String(value).replace(
  /\b(fileName|filename|nomeArquivo|nome_arquivo|originalFileName|original_filename)\s*[:=]\s*(?:"([^"]+)"|'([^']+)'|([^,;|}]+))/gi,
  (match, key, quotedDouble, quotedSingle, bareValue) => {
    const rawValue = String(quotedDouble || quotedSingle || bareValue || '').trim();
    const extensionMatch = rawValue.match(/(\.[a-z0-9]{1,10})$/i);
    const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';
    return `${key}=[ARQUIVO_PROTEGIDO]${extension}`;
  }
);

export const sanitizeSystemLogText = (value, maxLength = 800) => {
  let text = String(value ?? '')
    .normalize('NFC')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  text = sanitizeLabeledFileNames(text);
  text = text
    .replace(/(Bearer\s+)[a-z0-9._~+/=-]+/gi, '$1[DADO_PROTEGIDO]')
    .replace(/([?&](?:token|access_token|refresh_token|id_token|key|signature|senha|password)=)[^&\s]+/gi, '$1[DADO_PROTEGIDO]')
    .replace(/("?(?:token|accessToken|refreshToken|idToken|senha|password|secret|authorization|cpf|email|telefone|phone|endereco|address|nome|name)"?\s*[:=]\s*["'])[^"',}\s]+(?:\s+[^"',}]+)?/gi, '$1[DADO_PROTEGIDO]')
    .replace(/\b(token|accessToken|refreshToken|idToken|senha|password|secret|authorization)\s*[:=]\s*[^\s,}]+/gi, '$1=[DADO_PROTEGIDO]')
    .replace(/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g, '***.***.***-**')
    .replace(/\b\d{11}\b/g, '***.***.***-**')
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[EMAIL_PROTEGIDO]')
    .replace(/(?:\+?55[\s.-]*)?\(?\d{2}\)?[\s.-]*9?\d{4}[-.\s]\d{4}\b/g, '[TELEFONE_PROTEGIDO]')
    .replace(/\b(matr[ií]cula)\s*[:=]\s*[a-z0-9._/-]+/gi, '$1=[DADO_PROTEGIDO]')
    .replace(/\b(nome|name|telefone|phone|endereco|address)\s*[:=]\s*(?:"[^"]*"|'[^']*'|[^,;|}]+)/gi, '$1=[DADO_PROTEGIDO]')
    .replace(/([A-Z]:\\Users\\|\/Users\/|\/home\/)[^\\/\s]+/gi, '$1[USUARIO_PROTEGIDO]')
    .replace(/(https?:\/\/[^\s?]+)\?[^\s]+/gi, '$1?[PARAMETROS_PROTEGIDOS]');

  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const normalizeTechnicalIdentifier = (value, maxLength = 120) => sanitizeSystemLogText(value, maxLength)
  .replace(/[^a-z0-9_.:#-]+/gi, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '');

export const describeSystemLogTarget = (target, action = 'click') => {
  if (!target) return `${action}:unknown`;

  const candidate = typeof target.closest === 'function'
    ? target.closest('[data-log-action],button,a,input,select,textarea,form,[role="button"]') || target
    : target;
  const tag = normalizeTechnicalIdentifier(candidate.tagName || 'element', 30).toLowerCase() || 'element';
  const configuredAction = candidate.getAttribute
    ? normalizeTechnicalIdentifier(candidate.getAttribute('data-log-action'), 120)
    : '';
  const type = candidate.getAttribute
    ? normalizeTechnicalIdentifier(candidate.getAttribute('type'), 50)
    : '';

  if (configuredAction) return `action:${configuredAction}`;
  return `${action}:${tag}${type ? `[type=${type}]` : ''}`;
};

export const getSystemLogBuildMetadata = (portal, fallbackVersion) => {
  const systemVersion = readBuildValue('REACT_APP_SYSTEM_VERSION') || String(fallbackVersion || '0.0.0');
  const environment = readBuildValue('REACT_APP_SYSTEM_ENVIRONMENT')
    || readBuildValue('NODE_ENV')
    || 'browser';
  const configuredBuildId = readBuildValue('REACT_APP_BUILD_ID');

  return {
    schemaVersion: SYSTEM_LOG_SCHEMA_VERSION,
    portal,
    environment,
    systemVersion,
    version: systemVersion,
    buildId: configuredBuildId || `${environment === 'production' ? 'build-metadata-missing' : 'local'}-${portal}-${systemVersion}`,
    buildDate: readBuildValue('REACT_APP_BUILD_DATE') || null,
  };
};

export const normalizeSystemLogEventKind = (value, fallback = 'technical_error') => {
  return SYSTEM_LOG_EVENT_KINDS.includes(value) ? value : fallback;
};

export const inferSystemLogEventKind = ({
  eventKind,
  type,
  code,
  message,
  level,
} = {}) => {
  if (SYSTEM_LOG_EVENT_KINDS.includes(eventKind)) return eventKind;

  const signature = `${type || ''} ${code || ''} ${message || ''}`.toLowerCase();

  if (/auth\/(?:invalid-credential|wrong-password|user-not-found)|invalid credential/.test(signature)) {
    return 'user_input';
  }
  if (/auth\/too-many-requests/.test(signature)) return 'security_event';
  if (/processos internos não está ativo para este município/.test(signature)) return 'business_validation';
  if (/o acesso deste coordenador está inativo ou revogado|o acesso deste adjunto está suspenso ou revogado|a delegação (?:deste|do) adjunto (?:está vencida|não está ativa neste período)|o termo de delegação do adjunto não está ativo|perfil do dp sem acesso ao município|permissão de processos internos não concedida|perfil de portarias inativo|secretário inativo|coordenador sem delegação específica para processos internos|termo do coordenador não autoriza processos internos|perfil não autorizado para processos internos|município diferente do autorizado para o usuário|ação restrita ao departamento pessoal|processo fora da etapa de portaria ou contrato|processo de outra unidade gestora|processo não atribuído ao coordenador|pendências de assinatura são exclusivas do departamento pessoal/.test(signature)) {
    return 'access_rejected';
  }
  if (/business_validation|regra_de_negocio|expected_business_rule/.test(signature)) return 'business_validation';
  if (/user_input|invalid_argument|invalid-input|campo_invalido|campo obrigat[oó]rio|preencha|selecione|acima do m[aá]ximo|abaixo do m[ií]nimo|quantidade inv[aá]lida/.test(signature)) {
    return 'user_input';
  }
  if (/access_rejected|expected_permission_denied|unauthorized_attempt|delegação do coordenador inativa|usuário sem perfil ativo/.test(signature)) {
    return 'access_rejected';
  }
  if (/permission-denied|permission_denied|permission denied|missing or insufficient permissions/.test(signature)) {
    return 'access_rejected';
  }
  if (/webchannel|network-request-failed|failed to fetch|networkerror|unavailable|timeout|offline|could not reach cloud firestore backend|backend didn't respond|connection to indexed database server lost/.test(signature)) {
    return 'transient_failure';
  }
  if (/external_failure|script error\.|browser_extension|extension:\/\/|darkreader/.test(signature)) return 'external_failure';
  if (/diagnostic_warning/.test(signature) || level === 'warn') return 'diagnostic_warning';
  return 'technical_error';
};

export const normalizeSystemLogSeverity = (value, eventKind, level = 'error') => {
  if (SYSTEM_LOG_SEVERITIES.includes(value)) return value;
  if (eventKind === 'technical_error') return level === 'error' ? 'high' : 'medium';
  if (eventKind === 'security_event' || eventKind === 'access_rejected') return 'medium';
  if (eventKind === 'transient_failure' || eventKind === 'external_failure') return 'low';
  return 'informational';
};

export const inferSystemLogCategory = ({
  category,
  eventKind,
  type,
  code,
  message,
} = {}) => {
  if (category) return category;
  const signature = `${type || ''} ${code || ''} ${message || ''}`.toLowerCase();
  if (/@firebase\/database|firebase realtime database|realtime database|firebase database|firebase_database_permission_denied|database.*permission_denied|permission_denied.*database/.test(signature)) return 'firebase_database';
  if (/storage\/|firebase_storage|storage permission|storage unauthorized/.test(signature)) return 'firebase_storage';
  if (/firestore\/permission-denied|firestore_permission_denied|@firebase\/firestore|cloud firestore|missing or insufficient permissions/.test(signature)) return 'firestore_rules';
  if (/firestore/.test(signature)) return 'firestore';
  if (/permission-denied|permission_denied|permission denied/.test(signature) || eventKind === 'access_rejected') return 'access_control';
  if (/auth\/|firebase_auth/.test(signature) || eventKind === 'security_event') return 'firebase_authentication';
  if (/functions\/|cloud function|https callable/.test(signature)) return 'cloud_functions';
  if (/react_error|react|component stack/.test(signature)) return 'react';
  if (/loading chunk|chunkloaderror|service worker|serviceworker|cache/.test(signature)) return 'cache_service_worker';
  if (/webchannel|network|failed to fetch|timeout|offline|backend didn't respond/.test(signature)) return 'network';
  if (eventKind === 'business_validation' || eventKind === 'user_input') return 'business_rule';
  if (eventKind === 'external_failure') return 'browser_external';
  return 'javascript';
};

export const getSafeBrowserLocation = () => {
  if (typeof window === 'undefined' || !window.location) return { url: 'N/A', route: 'N/A' };
  const { origin, pathname } = window.location;
  return {
    url: origin && origin !== 'null' ? `${origin}${pathname}` : pathname,
    route: pathname || '/',
  };
};
